import React, { useMemo, useRef } from 'react';
import { PencilSquareIcon, TrashIcon, RectangleStackIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
    createTable,
    RenderingPresetType,
    TableColumnSpec,
    TableHeaderSpec,
} from '../../../../../components/table/CreateTable.tsx';
import { PrimaryButton } from '../../../../../components/general/PrimaryButton.tsx';
import { Tag } from '../../../../../components/general/Tag.tsx';
import { CatalogItem, CATALOG_UNIT_LABELS, VAT_SHORT_LABELS } from './types.ts';

interface CatalogTableProps {
    items: CatalogItem[];
    isLoading: boolean;
    onEdit: (item: CatalogItem) => void;
    onDelete: (item: CatalogItem) => void;
    onCreateClick: () => void;
}

export default function CatalogTable({ items, isLoading, onEdit, onDelete, onCreateClick }: CatalogTableProps) {
    // The table component must keep the same identity across parent re-renders,
    // otherwise its internal search state resets every time a modal opens/closes.
    // Handlers go through a ref so the memoized spec never sees stale closures.
    const handlersRef = useRef({ onEdit, onDelete, onCreateClick });
    handlersRef.current = { onEdit, onDelete, onCreateClick };

    const TableComponent = useMemo(() => {
        const columns: TableColumnSpec<CatalogItem>[] = [
            {
                name: 'Naam',
                field: 'name',
                style: { widthMultiplier: 2.5 },
                customRender: (row) => (
                    <span className="font-medium text-gray-900">{row.name}</span>
                ),
            },
            {
                name: 'Stukprijs',
                field: 'unit_price_cents',
                renderingPreset: RenderingPresetType.Money,
                style: { align: 'right' },
            },
            {
                name: 'Btw',
                field: 'vat_option',
                customRender: (row) => <span>{VAT_SHORT_LABELS[row.vat_option] ?? row.vat_option}</span>,
            },
            {
                name: 'Eenheid',
                field: 'unit',
                customRender: (row) => (
                    <Tag variant="secondary">{CATALOG_UNIT_LABELS[row.unit] ?? row.unit}</Tag>
                ),
            },
            {
                name: 'Gebruikt',
                field: 'times_used',
                style: { align: 'right' },
                customRender: (row) => (
                    <span className={row.times_used > 0 ? 'text-gray-700' : 'text-gray-400'}>
                        {row.times_used}×
                    </span>
                ),
            },
            {
                name: '',
                field: 'id',
                disableSorting: true,
                style: { widthMultiplier: 0.7, align: 'right', skeleton: 'none' },
                customRender: (row) => (
                    <div className="flex justify-end gap-1 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                            type="button"
                            aria-label={`Bewerk ${row.name}`}
                            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                handlersRef.current.onEdit(row);
                            }}
                        >
                            <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            aria-label={`Verwijder ${row.name}`}
                            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                handlersRef.current.onDelete(row);
                            }}
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>
                    </div>
                ),
            },
        ];

        const tableSpec: TableHeaderSpec<CatalogItem> = {
            columns,
            isLoading,
            skeletonRows: 5,
            search: {
                searchFn: (row, searchState) => {
                    const query = searchState.currentQuery.trim().toLowerCase();
                    if (!query) return true;
                    return (
                        row.name.toLowerCase().includes(query) ||
                        (CATALOG_UNIT_LABELS[row.unit] ?? '').toLowerCase().includes(query)
                    );
                },
            },
            onRowClick: (row) => handlersRef.current.onEdit(row),
            options: {
                allowFullRowClick: true,
            },
            onEmptyRender: () => (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                    <RectangleStackIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <h3 className="mt-3 text-sm font-semibold text-gray-900">
                        Nog geen producten of diensten
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
                        Sla je diensten en producten één keer op met prijs en btw-tarief, zodat je ze
                        bij het factureren direct bij de hand hebt.
                    </p>
                    <div className="mt-5 flex justify-center">
                        <PrimaryButton
                            title="Nieuw item"
                            icon={PlusIcon}
                            onClick={() => handlersRef.current.onCreateClick()}
                        />
                    </div>
                </div>
            ),
        };

        return createTable<CatalogItem>([], tableSpec);
    }, [isLoading]);

    return <TableComponent data={items} />;
}
